/**
 * URL state for `/reports/campaigns`.
 * Date params: Organization-timezone calendar days as YYYY-MM-DD.
 */
import {
  DEFAULT_REPORT_CAMPAIGN_PAGE_SIZE,
  DEFAULT_REPORT_CAMPAIGN_SORT,
  REPORT_CAMPAIGN_PAGE_SIZES,
  REPORT_CAMPAIGN_SORTS,
  REPORTABLE_CAMPAIGN_STATUSES,
  type ReportableCampaignStatusName,
  type ReportCampaignPageSize,
  type ReportCampaignSortName,
} from "@multi-chat/shared";

const YMD_RE = /^\d{4}-\d{2}-\d{2}$/;

export type CampaignReportQuery = {
  from?: string;
  to?: string;
  search: string;
  status?: ReportableCampaignStatusName;
  endpointId?: number;
  template?: string;
  page: number;
  pageSize: ReportCampaignPageSize;
  sort: ReportCampaignSortName;
};

function parseYmd(value: string | null): string | undefined {
  if (!value || !YMD_RE.test(value)) return undefined;
  return value;
}

function parsePositiveInt(value: string | null): number | undefined {
  if (!value) return undefined;
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return undefined;
  return n;
}

function parsePageSize(value: string | null): ReportCampaignPageSize {
  const n = Number(value);
  if ((REPORT_CAMPAIGN_PAGE_SIZES as readonly number[]).includes(n)) {
    return n as ReportCampaignPageSize;
  }
  return DEFAULT_REPORT_CAMPAIGN_PAGE_SIZE;
}

function parseSort(value: string | null): ReportCampaignSortName {
  if (value && (REPORT_CAMPAIGN_SORTS as readonly string[]).includes(value)) {
    return value as ReportCampaignSortName;
  }
  return DEFAULT_REPORT_CAMPAIGN_SORT;
}

function parseStatus(value: string | null): ReportableCampaignStatusName | undefined {
  if (value && (REPORTABLE_CAMPAIGN_STATUSES as readonly string[]).includes(value)) {
    return value as ReportableCampaignStatusName;
  }
  return undefined;
}

export function parseCampaignReportQuery(search: string): CampaignReportQuery {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  return {
    from: parseYmd(params.get("from")),
    to: parseYmd(params.get("to")),
    search: params.get("search")?.trim() ?? "",
    status: parseStatus(params.get("status")),
    endpointId: parsePositiveInt(params.get("endpointId")),
    template: params.get("template")?.trim() || undefined,
    page: Math.max(1, parsePositiveInt(params.get("page")) ?? 1),
    pageSize: parsePageSize(params.get("pageSize")),
    sort: parseSort(params.get("sort")),
  };
}

export function campaignReportHref(query: CampaignReportQuery): string {
  const params = new URLSearchParams();
  if (query.from) params.set("from", query.from);
  if (query.to) params.set("to", query.to);
  if (query.search) params.set("search", query.search);
  if (query.status) params.set("status", query.status);
  if (query.endpointId != null) params.set("endpointId", String(query.endpointId));
  if (query.template) params.set("template", query.template);
  if (query.page > 1) params.set("page", String(query.page));
  if (query.pageSize !== DEFAULT_REPORT_CAMPAIGN_PAGE_SIZE) {
    params.set("pageSize", String(query.pageSize));
  }
  if (query.sort !== DEFAULT_REPORT_CAMPAIGN_SORT) {
    params.set("sort", query.sort);
  }
  const qs = params.toString();
  return qs.length > 0 ? `/reports/campaigns?${qs}` : "/reports/campaigns";
}

export function hasActiveCampaignFilters(query: CampaignReportQuery): boolean {
  return Boolean(
    query.search ||
      query.status ||
      query.endpointId != null ||
      query.template ||
      query.from ||
      query.to,
  );
}

export function clearCampaignFilters(query: CampaignReportQuery): CampaignReportQuery {
  return {
    search: "",
    page: 1,
    pageSize: query.pageSize,
    sort: query.sort,
  };
}
